// ══════════════════════════════════════════════════════════════════════════
// EVSE Field Configuration
// Single source of truth for the Add EVSE checklist and dynamic form.
// Both the checklist panel and the form panel read from this file.
// ══════════════════════════════════════════════════════════════════════════

export const REQUIRED_FIELDS = [
  { name: 'ocpp_id', label: 'OCPP ID', type: 'text', placeholder: 'e.g. SRLK-001',
    hint: 'Must match exactly what the charger uses to identify itself via OCPP.' },
  { name: 'name', label: 'Station Name', type: 'text', placeholder: 'e.g. Union Street Station' },
  { name: 'location', label: 'Location', type: 'text', placeholder: 'e.g. Union Street, Aberdeen, AB10 1AB' },
  { name: 'connector_type', label: 'Connector Type', type: 'select',
    options: ['Type 2', 'CCS', 'CHAdeMO', 'Type 1'] },
];

export const FIELD_GROUPS = [
  {
    key: 'location_details',
    title: 'Location Details',
    icon: 'pin',
    fields: [
      { name: 'address',   label: 'Address',   type: 'text', placeholder: 'e.g. 12 Union Street' },
      { name: 'city',      label: 'City',      type: 'text', placeholder: 'e.g. Aberdeen' },
      { name: 'country',   label: 'Country',   type: 'text', placeholder: 'e.g. United Kingdom' },
      { name: 'zipcode',   label: 'Post Code', type: 'text', placeholder: 'e.g. AB10 1AB' },
      { name: 'latitude',  label: 'Latitude',  type: 'text', placeholder: 'e.g. 57.1497' },
      { name: 'longitude', label: 'Longitude', type: 'text', placeholder: 'e.g. -2.0943' },
    ]
  },
  {
    key: 'network_org',
    title: 'Network & Organisation',
    icon: 'network',
    fields: [
      { name: 'network_type', label: 'Network Type', type: 'select',
        options: ['Public', 'Private', 'Restricted Network'] },
      { name: 'organisation_name', label: 'Organisation Name', type: 'text', placeholder: 'e.g. SRLK EV Charge Ltd' },
      { name: 'ocpp_version', label: 'OCPP Version', type: 'select',
        options: ['1.6', '2.0.1'] },
      { name: 'oem_name', label: 'OEM / Manufacturer Name', type: 'text', placeholder: 'e.g. BENY' },
    ]
  },
  {
    key: 'operational',
    title: 'Operational Details',
    icon: 'settings',
    fields: [
      { name: 'free_charging', label: 'Free Charging', type: 'select', options: ['Yes', 'No'] },
      { name: 'plug_and_charge', label: 'Plug and Charge', type: 'select', options: ['Yes', 'No'] },
      { name: 'parking_type', label: 'Parking Type', type: 'select',
        options: ['On Street Parking', 'Off Street Parking', 'Private', 'Public'] },
      { name: 'max_ampere', label: 'Max Ampere', type: 'text', placeholder: 'e.g. 32' },
      { name: 'max_voltage', label: 'Max Voltage', type: 'text', placeholder: 'e.g. 230' },
    ]
  },
  {
    key: 'extras',
    title: 'Extras',
    icon: 'star',
    fields: [
      { name: 'amenities', label: 'Amenities', type: 'text', placeholder: 'e.g. WiFi, Parking, Restaurant' },
      { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Any additional notes about this EVSE...' },
    ]
  },
];

export const ALL_OPTIONAL_FIELDS = FIELD_GROUPS.flatMap(g => g.fields);

// ── STEP 1: LOCATION (used by the multi-step EVSE wizard) ──────────────────
export const LOCATION_REQUIRED_FIELDS = [
  { name: 'station_name', label: 'Station Name', type: 'text', placeholder: 'e.g. Union Street Car Park' },
];

export const LOCATION_FIELD_GROUPS = [
  {
    key: 'location_details',
    title: 'Location Details',
    icon: 'pin',
    fields: [
      { name: 'address',   label: 'Address',   type: 'text', placeholder: 'e.g. 12 Union Street' },
      { name: 'city',      label: 'City',      type: 'text', placeholder: 'e.g. Aberdeen' },
      { name: 'country',   label: 'Country',   type: 'text', placeholder: 'e.g. United Kingdom' },
      { name: 'zipcode',   label: 'Post Code', type: 'text', placeholder: 'e.g. AB10 1AB' },
      { name: 'latitude',  label: 'Latitude',  type: 'text', placeholder: 'e.g. 57.1497' },
      { name: 'longitude', label: 'Longitude', type: 'text', placeholder: 'e.g. -2.0943' },
    ]
  },
  {
    key: 'operational',
    title: 'Operational Details',
    icon: 'settings',
    fields: [
      { name: 'network_type', label: 'Network Type', type: 'select',
        options: ['Public', 'Private', 'Restricted Network'] },
      { name: 'parking_type', label: 'Parking Type', type: 'select',
        options: ['On Street Parking', 'Off Street Parking', 'Private', 'Public'] },
      { name: 'free_charging', label: 'Free Charging', type: 'select', options: ['Yes', 'No'] },
    ]
  },
  {
    key: 'extras',
    title: 'Extras',
    icon: 'star',
    fields: [
      { name: 'amenities', label: 'Amenities', type: 'text', placeholder: 'e.g. WiFi, Parking, Restaurant' },
      { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Any additional notes about this location...' },
    ]
  },
];

export const ALL_LOCATION_OPTIONAL_FIELDS = LOCATION_FIELD_GROUPS.flatMap(g => g.fields);

// ── STEP 2: CHARGER ──────────────────────────────────────────────────────────
export const CHARGER_REQUIRED_FIELDS = [
  { name: 'ocpp_id', label: 'EVSE ID (OCPP ID)', type: 'text', placeholder: 'e.g. SRLK-001',
    hint: 'Must match exactly what the charger uses to identify itself via OCPP.' },
];

export const CHARGER_FIELD_GROUPS = [
  {
    key: 'charger_details',
    title: 'Charger Details',
    icon: 'network',
    fields: [
      { name: 'oem_name', label: 'OEM / Manufacturer Name', type: 'text', placeholder: 'e.g. BENY' },
      { name: 'ocpp_version', label: 'OCPP Version', type: 'select', options: ['1.6', '2.0.1'] },
      { name: 'plug_and_charge', label: 'Plug and Charge', type: 'select', options: ['Yes', 'No'] },
      { name: 'qr_code_id', label: 'QR Code ID', type: 'text', placeholder: 'e.g. SRLK-QR-00123',
        hint: 'A unique code printed on this charger, used for the tap-to-pay QR flow.' },
    ]
  },
];

export const ALL_CHARGER_OPTIONAL_FIELDS = CHARGER_FIELD_GROUPS.flatMap(g => g.fields);

// ── STEP 3: CONNECTOR ────────────────────────────────────────────────────────
export const CONNECTOR_REQUIRED_FIELDS = [
  { name: 'connector_type', label: 'Connector Type', type: 'select',
    options: ['Type 2', 'CCS', 'CHAdeMO', 'Type 1'] },
  { name: 'max_ampere', label: 'Max Ampere', type: 'text', placeholder: 'e.g. 32' },
];

export const CONNECTOR_FIELD_GROUPS = [
  {
    key: 'connector_details',
    title: 'Connector Details',
    icon: 'settings',
    fields: [
      { name: 'max_voltage', label: 'Max Voltage', type: 'text', placeholder: 'e.g. 230' },
      { name: 'charging_speed', label: 'Charging Speed', type: 'select',
        options: ['Slow', 'Fast', 'Rapid', 'Ultra-Rapid'] },
      { name: 'energy_capacity', label: 'Energy Capacity', type: 'text', placeholder: 'e.g. 7.4 kW' },
      { name: 'power_type', label: 'Power Type', type: 'select', options: ['AC', 'DC'] },
    ]
  },
];

export const ALL_CONNECTOR_OPTIONAL_FIELDS = CONNECTOR_FIELD_GROUPS.flatMap(g => g.fields);

// ── Field metadata lookup — used by the bulk upload preview ────────────────
export function getFieldMeta(level, fieldName) {
  const sources = {
    location: [...LOCATION_REQUIRED_FIELDS, ...ALL_LOCATION_OPTIONAL_FIELDS],
    charger: [...CHARGER_REQUIRED_FIELDS, ...ALL_CHARGER_OPTIONAL_FIELDS],
    connector: [...CONNECTOR_REQUIRED_FIELDS, ...ALL_CONNECTOR_OPTIONAL_FIELDS],
  };
  const list = sources[level] || [];
  return list.find(f => f.name === fieldName) || { name: fieldName, label: fieldName, type: 'text' };
}